import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './devices-page.css';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import DevicesTable, { COLUMN_DEFS, CLOUD_COLUMN_DEFS } from './components/DevicesTable.jsx';
import WanderingDevicesTable, { WANDERING_COLUMN_DEFS } from './components/WanderingDevicesTable.jsx';
import SpeakerDevicesTable, { SPEAKER_COLUMN_DEFS } from './components/SpeakerDevicesTable.jsx';
import SystemDevicesTable, { SYSTEM_COLUMN_DEFS } from './components/SystemDevicesTable.jsx';
import ServersTable, { SERVER_COLUMN_DEFS, SERVER_CLOUD_COLUMN_DEFS } from './components/ServersTable.jsx';
import usePersistedColumns from '../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../hooks/usePersistedPerPage.js';
import ServerDrawer from './components/ServerDrawer.jsx';
import AddDeviceDrawer from './components/AddDeviceDrawer.jsx';
import AddWanderingDeviceDrawer from './components/AddWanderingDeviceDrawer.jsx';
import AddSpeakerDeviceDrawer from './components/AddSpeakerDeviceDrawer.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../community/residents/components/ActiveFilterBar.jsx';
import ColumnVisibilityToggle from '../community/residents/components/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../community/residents/components/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, getCampusId, getUserId, getSessionId,
  getCampusList, getRoleWeight, getCampusConfig, updateSessionCampus,
} from '../../services/session.js';
import { fetchDevices, unassignDevices, deleteDevices, addDevice, fetchWanderingDevices, fetchSpeakerDevices, fetchSpeakerDeviceTypes, fetchSpeakerDeviceModels, addSpeakerDevice, fetchSystemDevices, deleteSystemDevices, fetchServers, fetchServerAreas, addServer, deleteServers } from './api/devicesApi.js';
import { exportDevicesCsv, exportWanderingDevicesCsv, exportSpeakerDevicesCsv, exportSystemDevicesCsv, exportServersCsv } from '../../services/csvExport.js';
import { iAmHigherAuthority as iAmHigherAuth } from '../../libs.jsx';

/** Default visible columns for Nurse Call legacy */
const DEFAULT_LEGACY_VISIBLE = COLUMN_DEFS.map((c) => c.key);
const ALL_LEGACY_KEYS = COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for Nurse Call cloud */
const DEFAULT_CLOUD_VISIBLE = CLOUD_COLUMN_DEFS.map((c) => c.key);
const ALL_CLOUD_KEYS = CLOUD_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for Wandering tab */
const DEFAULT_WD_VISIBLE = WANDERING_COLUMN_DEFS.map((c) => c.key);
const ALL_WD_KEYS = WANDERING_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for Speaker Devices tab */
const DEFAULT_SP_VISIBLE = SPEAKER_COLUMN_DEFS.map((c) => c.key);
const ALL_SP_KEYS = SPEAKER_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for System Devices tab */
const DEFAULT_SD_VISIBLE = SYSTEM_COLUMN_DEFS.map((c) => c.key);
const ALL_SD_KEYS = SYSTEM_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for Servers tab (non-cloud) */
const DEFAULT_SV_VISIBLE = SERVER_COLUMN_DEFS.map((c) => c.key);
const ALL_SV_KEYS = SERVER_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for Servers tab (cloud) */
const DEFAULT_SV_CLOUD_VISIBLE = SERVER_CLOUD_COLUMN_DEFS.map((c) => c.key);
const ALL_SV_CLOUD_KEYS = SERVER_CLOUD_COLUMN_DEFS.map((c) => c.key);

/**
 * DevicesPage — V2 multi-tab orchestrator for the Devices page.
 *
 * Loads all four device types (Nurse Call, Wandering, System Devices, Servers)
 * simultaneously on mount. Tab switching is instant — no re-fetching.
 * Each tab has independent state for search, filters, sort, pagination,
 * selection, and visible columns.
 */
const DevicesPage = ({ initialTab = 'nursecall' }) => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const myUserId = useMemo(() => getUserId(), []);

  const isCloud = useMemo(() => !!getCampusConfig()?.is_cloud, [campusId]);
  const isGrandstream = useMemo(() => !!getCampusConfig()?.is_grandstream, [campusId]);
  const tzOffset = useMemo(() => parseInt(getCampusConfig()?.tz_offset ?? 0, 10) || 0, [campusId]);
  const addRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const editRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const deleteRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  // System Devices / Speaker Devices / Servers tabs require >80 for edit + delete
  const restrictedEditRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  const ncColumnDefs = isCloud ? CLOUD_COLUMN_DEFS : COLUMN_DEFS;
  const ncDefaultVisible = isCloud ? DEFAULT_CLOUD_VISIBLE : DEFAULT_LEGACY_VISIBLE;
  const svColumnDefs = isCloud ? SERVER_CLOUD_COLUMN_DEFS : SERVER_COLUMN_DEFS;
  const svDefaultVisible = isCloud ? DEFAULT_SV_CLOUD_VISIBLE : DEFAULT_SV_VISIBLE;

  /** Builds the session params object used by API calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // ==========================================================================
  // TAB
  // ==========================================================================
  const activeTab = initialTab;

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // NURSE CALL (nc) STATE
  // ==========================================================================
  const [ncData, setNcData] = useState([]);
  const [ncLoading, setNcLoading] = useState(true);
  const [ncSearch, setNcSearch] = useState('');
  const [ncFilters, setNcFilters] = useState({});
  const [ncSortColumn, setNcSortColumn] = useState('descriptions');
  const [ncSortOrder, setNcSortOrder] = useState('asc');
  const [ncPage, setNcPage] = useState(1);
  const [ncPerPage, setNcPerPage] = usePersistedPerPage();
  const [ncLegacyVisible, handleNcLegacyToggle] = usePersistedColumns('devices-nc', DEFAULT_LEGACY_VISIBLE, ALL_LEGACY_KEYS);
  const [ncCloudVisible, handleNcCloudToggle] = usePersistedColumns('devices-nc-cloud', DEFAULT_CLOUD_VISIBLE, ALL_CLOUD_KEYS);
  const ncVisibleColumns = isCloud ? ncCloudVisible : ncLegacyVisible;
  const handleNcColumnToggle = isCloud ? handleNcCloudToggle : handleNcLegacyToggle;
  const [ncSelectedIds, setNcSelectedIds] = useState([]);
  const [ncShowActiveOnly, setNcShowActiveOnly] = useState(false);
  const [ncDrawerOpen, setNcDrawerOpen] = useState(false);

  // Auto-open the Add Device drawer when navigated here with
  // `state.openAddDevice` (e.g. the "Add a device" link in the device-assign
  // empty states). Guarded so it only fires once per mount.
  const location = useLocation();
  const autoOpenedAddRef = useRef(false);
  useEffect(() => {
    if (!autoOpenedAddRef.current && location.state?.openAddDevice && activeTab === 'nursecall') {
      autoOpenedAddRef.current = true;
      setNcDrawerOpen(true);
    }
  }, [location.state, activeTab]);

  // ==========================================================================
  // WANDERING (wd) STATE
  // ==========================================================================
  const [wdData, setWdData] = useState([]);
  const [wdLoading, setWdLoading] = useState(true);
  const [wdSearch, setWdSearch] = useState('');
  const [wdFilters, setWdFilters] = useState({});
  const [wdSortColumn, setWdSortColumn] = useState('descriptions');
  const [wdSortOrder, setWdSortOrder] = useState('asc');
  const [wdPage, setWdPage] = useState(1);
  const [wdPerPage, setWdPerPage] = usePersistedPerPage();
  const [wdVisibleColumns, handleWdColumnToggle] = usePersistedColumns('devices-wandering', DEFAULT_WD_VISIBLE, ALL_WD_KEYS);
  const [wdSelectedIds, setWdSelectedIds] = useState([]);
  const [wdShowActiveOnly, setWdShowActiveOnly] = useState(false);
  const [wdDrawerOpen, setWdDrawerOpen] = useState(false);

  // ==========================================================================
  // SPEAKER DEVICES (sp) STATE
  // ==========================================================================
  const [spData, setSpData] = useState([]);
  const [spLoading, setSpLoading] = useState(true);
  const [spSearch, setSpSearch] = useState('');
  const [spFilters, setSpFilters] = useState({});
  const [spSortColumn, setSpSortColumn] = useState('descriptions');
  const [spSortOrder, setSpSortOrder] = useState('asc');
  const [spPage, setSpPage] = useState(1);
  const [spPerPage, setSpPerPage] = usePersistedPerPage();
  const [spVisibleColumns, handleSpColumnToggle] = usePersistedColumns('devices-speaker', DEFAULT_SP_VISIBLE, ALL_SP_KEYS);
  const [spSelectedIds, setSpSelectedIds] = useState([]);
  const [spShowActiveOnly, setSpShowActiveOnly] = useState(false);
  const [spDrawerOpen, setSpDrawerOpen] = useState(false);
  const [spDeviceTypes, setSpDeviceTypes] = useState([]);
  const [spModels, setSpModels] = useState([]);

  // ==========================================================================
  // SYSTEM DEVICES (sd) STATE
  // ==========================================================================
  const [sdData, setSdData] = useState([]);
  const [sdLoading, setSdLoading] = useState(true);
  const [sdSearch, setSdSearch] = useState('');
  const [sdFilters, setSdFilters] = useState({});
  const [sdSortColumn, setSdSortColumn] = useState('device_name');
  const [sdSortOrder, setSdSortOrder] = useState('asc');
  const [sdPage, setSdPage] = useState(1);
  const [sdPerPage, setSdPerPage] = usePersistedPerPage();
  const [sdVisibleColumns, handleSdColumnToggle] = usePersistedColumns('devices-system', DEFAULT_SD_VISIBLE, ALL_SD_KEYS);
  const [sdSelectedIds, setSdSelectedIds] = useState([]);
  const [sdDrawerOpen, setSdDrawerOpen] = useState(false);

  // System Device drawer — area-only, category IDs 4 & 5
  const [sdCategoryConfig] = useState(() => ({ area: new Set([4, 5]) }));

  // ==========================================================================
  // SERVERS (sv) STATE
  // ==========================================================================
  const [svData, setSvData] = useState([]);
  const [svLoading, setSvLoading] = useState(true);
  const [svSearch, setSvSearch] = useState('');
  const [svFilters, setSvFilters] = useState({});
  const [svSortColumn, setSvSortColumn] = useState('description');
  const [svSortOrder, setSvSortOrder] = useState('asc');
  const [svPage, setSvPage] = useState(1);
  const [svPerPage, setSvPerPage] = usePersistedPerPage();
  const [svLegacyVisible, handleSvLegacyToggle] = usePersistedColumns('devices-servers', DEFAULT_SV_VISIBLE, ALL_SV_KEYS);
  const [svCloudVisible, handleSvCloudToggle] = usePersistedColumns('devices-servers-cloud', DEFAULT_SV_CLOUD_VISIBLE, ALL_SV_CLOUD_KEYS);
  const svVisibleColumns = isCloud ? svCloudVisible : svLegacyVisible;
  const handleSvColumnToggle = isCloud ? handleSvCloudToggle : handleSvLegacyToggle;
  const [svSelectedIds, setSvSelectedIds] = useState([]);
  const [svAreas, setSvAreas] = useState([]);
  const [svDrawerOpen, setSvDrawerOpen] = useState(false);

  // ==========================================================================
  // DATA LOADING — All Four in Parallel
  // ==========================================================================
  const loadNurseCallDevices = useCallback(async (readOnly = true) => {
    setNcLoading(true);
    try {
      const data = await fetchDevices(campusId, readOnly);
      setNcData(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load nurse call devices:', err);
    } finally {
      setNcLoading(false);
    }
  }, [campusId]);

  const loadWanderingDevices = useCallback(async (readOnly = true) => {
    setWdLoading(true);
    try {
      const data = await fetchWanderingDevices(campusId, readOnly);
      setWdData(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load wandering devices:', err);
    } finally {
      setWdLoading(false);
    }
  }, [campusId]);

  const loadSpeakerDevices = useCallback(async (readOnly = true) => {
    if (!isGrandstream) {
      setSpLoading(false);
      return;
    }
    setSpLoading(true);
    try {
      const data = await fetchSpeakerDevices(campusId, readOnly);
      setSpData(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load speaker devices:', err);
    } finally {
      setSpLoading(false);
    }
  }, [campusId, isGrandstream]);

  const loadSpeakerLookups = useCallback(async () => {
    if (!isGrandstream) return;
    try {
      const [types, models] = await Promise.all([
        fetchSpeakerDeviceTypes(),
        fetchSpeakerDeviceModels(),
      ]);
      setSpDeviceTypes(types || []);
      setSpModels(models || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load speaker lookups:', err);
    }
  }, [isGrandstream]);

  const loadSystemDevices = useCallback(async (readOnly = true) => {
    setSdLoading(true);
    try {
      const data = await fetchSystemDevices(campusId, readOnly);
      setSdData(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load system devices:', err);
    } finally {
      setSdLoading(false);
    }
  }, [campusId]);

  const loadServers = useCallback(async (readOnly = true) => {
    setSvLoading(true);
    try {
      const data = await fetchServers(campusId, readOnly);
      setSvData(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load servers:', err);
    } finally {
      setSvLoading(false);
    }
  }, [campusId]);

  const loadServerAreas = useCallback(async () => {
    try {
      const data = await fetchServerAreas(campusId);
      setSvAreas(data || []);
    } catch (err) {
      console.error('[DevicesPage] Failed to load server areas:', err);
    }
  }, [campusId]);

  useEffect(() => { loadNurseCallDevices(); }, [loadNurseCallDevices]);
  useEffect(() => { loadWanderingDevices(); }, [loadWanderingDevices]);
  useEffect(() => { loadSpeakerDevices(); }, [loadSpeakerDevices]);
  useEffect(() => { loadSpeakerLookups(); }, [loadSpeakerLookups]);
  useEffect(() => { loadSystemDevices(); }, [loadSystemDevices]);
  useEffect(() => { loadServers(); }, [loadServers]);
  useEffect(() => { loadServerAreas(); }, [loadServerAreas]);

  // ==========================================================================
  // NURSE CALL — Filter / Search / Sort Pipeline
  // ==========================================================================
  const ncFiltered = useMemo(() => {
    let data = [...ncData];

    // 0. Active Only filter
    if (ncShowActiveOnly) {
      data = data.filter((d) => !!d.is_active);
    }

    // 1. Search
    if (ncSearch.trim()) {
      const s = ncSearch.toLowerCase();
      data = data.filter(
        (r) =>
          r.device_name?.toLowerCase().includes(s) ||
          r.descriptions?.toLowerCase().includes(s) ||
          r.manufacture_uuid?.toLowerCase().includes(s) ||
          r.inovonics_model_name?.toLowerCase().includes(s) ||
          r.area_name?.toLowerCase().includes(s) ||
          r.resident_name?.toLowerCase().includes(s) ||
          (r.residents || []).some((res) => res.resident_name?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(ncFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        // Residents column — filter by resident_name across the residents array
        if (columnKey === 'resident_name' || columnKey === 'residents') {
          const names = (row.residents || []).map((r) => r.resident_name || '');
          return values.some((v) => names.includes(v));
        }
        // Device health text filters
        if (columnKey === 'low_battery_text') {
          return values.includes(row.low_battery_text || '');
        }
        if (columnKey === 'tamper_text') {
          return values.includes(row.tamper_text || '');
        }
        if (columnKey === 'offline_text') {
          return values.includes(row.offline_text || '');
        }
        // Boolean active status
        if (columnKey === 'is_active') {
          return values.some((v) => {
            if (v === 'Yes') return !!row.is_active;
            if (v === 'No') return !row.is_active;
            return String(row.is_active) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '');
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[ncSortColumn];
      let bVal = b[ncSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Numeric sort for device health columns
      if (['low_battery', 'tamper', 'offline'].includes(ncSortColumn)) {
        const result = (Number(aVal) || 0) - (Number(bVal) || 0);
        return ncSortOrder === 'asc' ? result : -result;
      }

      // Date sort
      if (['last_alarm', 'last_active'].includes(ncSortColumn)) {
        const aDate = aVal ? new Date(aVal).getTime() : 0;
        const bDate = bVal ? new Date(bVal).getTime() : 0;
        const result = aDate - bDate;
        return ncSortOrder === 'asc' ? result : -result;
      }

      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return ncSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [ncData, ncShowActiveOnly, ncSearch, ncFilters, ncSortColumn, ncSortOrder]);

  // Nurse Call Pagination
  const ncTotalFiltered = ncFiltered.length;
  const ncTotalPages = Math.max(1, Math.ceil(ncTotalFiltered / ncPerPage));
  const ncSafePage = Math.min(ncPage, ncTotalPages);

  const ncPaginated = useMemo(() => {
    const start = (ncSafePage - 1) * ncPerPage;
    return ncFiltered.slice(start, start + ncPerPage);
  }, [ncFiltered, ncSafePage, ncPerPage]);

  const ncShowingStart = ncTotalFiltered === 0 ? 0 : (ncSafePage - 1) * ncPerPage + 1;
  const ncShowingEnd = Math.min(ncSafePage * ncPerPage, ncTotalFiltered);

  useEffect(() => { setNcPage(1); }, [ncSearch, ncFilters, ncPerPage, ncShowActiveOnly]);

  const ncPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, ncSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(ncTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [ncSafePage, ncTotalPages]);

  // ==========================================================================
  // WANDERING — Filter / Search / Sort Pipeline
  // ==========================================================================
  const wdFiltered = useMemo(() => {
    let data = [...wdData];

    // 0. Active Only filter
    if (wdShowActiveOnly) {
      data = data.filter((d) => !!d.is_active);
    }

    // 1. Search
    if (wdSearch.trim()) {
      const s = wdSearch.toLowerCase();
      data = data.filter((r) =>
        r.device_name?.toLowerCase().includes(s) ||
        r.descriptions?.toLowerCase().includes(s) ||
        r.manufacture_uuid?.toLowerCase().includes(s) ||
        r.model_name?.toLowerCase().includes(s) ||
        r.resident_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(wdFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        if (columnKey === 'is_active') {
          return values.some((v) => {
            if (v === 'Yes') return !!row.is_active;
            if (v === 'No') return !row.is_active;
            return String(row.is_active) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '');
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[wdSortColumn];
      let bVal = b[wdSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return wdSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [wdData, wdShowActiveOnly, wdSearch, wdFilters, wdSortColumn, wdSortOrder]);

  // Wandering Pagination
  const wdTotalFiltered = wdFiltered.length;
  const wdTotalPages = Math.max(1, Math.ceil(wdTotalFiltered / wdPerPage));
  const wdSafePage = Math.min(wdPage, wdTotalPages);

  const wdPaginated = useMemo(() => {
    const start = (wdSafePage - 1) * wdPerPage;
    return wdFiltered.slice(start, start + wdPerPage);
  }, [wdFiltered, wdSafePage, wdPerPage]);

  const wdShowingStart = wdTotalFiltered === 0 ? 0 : (wdSafePage - 1) * wdPerPage + 1;
  const wdShowingEnd = Math.min(wdSafePage * wdPerPage, wdTotalFiltered);

  useEffect(() => { setWdPage(1); }, [wdSearch, wdFilters, wdPerPage, wdShowActiveOnly]);

  const wdPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, wdSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(wdTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [wdSafePage, wdTotalPages]);

  // ==========================================================================
  // SPEAKER DEVICES — Filter / Search / Sort Pipeline
  // ==========================================================================
  const spFiltered = useMemo(() => {
    let data = [...spData];

    // 0. Active Only filter
    if (spShowActiveOnly) {
      data = data.filter((d) => !!d.is_active);
    }

    // 1. Search
    if (spSearch.trim()) {
      const s = spSearch.toLowerCase();
      data = data.filter((r) =>
        r.device_name?.toLowerCase().includes(s) ||
        r.descriptions?.toLowerCase().includes(s) ||
        r.manufacture_uuid?.toLowerCase().includes(s) ||
        r.model_name?.toLowerCase().includes(s) ||
        r.area_name?.toLowerCase().includes(s) ||
        (r.residents || []).some((res) => res.resident_name?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(spFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        if (columnKey === 'resident_name' || columnKey === 'residents') {
          const names = (row.residents || []).map((r) => r.resident_name || '');
          if (names.length === 0) return values.includes('(empty)');
          return values.some((v) => names.includes(v));
        }
        if (columnKey === 'is_active') {
          return values.some((v) => {
            if (v === 'Yes') return !!row.is_active;
            if (v === 'No') return !row.is_active;
            return String(row.is_active) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '');
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[spSortColumn];
      let bVal = b[spSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return spSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [spData, spShowActiveOnly, spSearch, spFilters, spSortColumn, spSortOrder]);

  // Speaker Pagination
  const spTotalFiltered = spFiltered.length;
  const spTotalPages = Math.max(1, Math.ceil(spTotalFiltered / spPerPage));
  const spSafePage = Math.min(spPage, spTotalPages);

  const spPaginated = useMemo(() => {
    const start = (spSafePage - 1) * spPerPage;
    return spFiltered.slice(start, start + spPerPage);
  }, [spFiltered, spSafePage, spPerPage]);

  const spShowingStart = spTotalFiltered === 0 ? 0 : (spSafePage - 1) * spPerPage + 1;
  const spShowingEnd = Math.min(spSafePage * spPerPage, spTotalFiltered);

  useEffect(() => { setSpPage(1); }, [spSearch, spFilters, spPerPage, spShowActiveOnly]);

  const spPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, spSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(spTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [spSafePage, spTotalPages]);

  // ==========================================================================
  // SYSTEM DEVICES — Filter / Search / Sort Pipeline
  // ==========================================================================
  const sdFiltered = useMemo(() => {
    let data = [...sdData];

    // NO active only filter for system devices

    // 1. Search
    if (sdSearch.trim()) {
      const s = sdSearch.toLowerCase();
      data = data.filter((r) =>
        r.device_name?.toLowerCase().includes(s) ||
        r.manufacture_uuid?.toLowerCase().includes(s) ||
        r.area_name?.toLowerCase().includes(s) ||
        r.model_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(sdFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        if (columnKey === 'is_active') {
          return values.some((v) => {
            if (v === 'Yes') return !!row.is_active;
            if (v === 'No') return !row.is_active;
            return String(row.is_active) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '');
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[sdSortColumn];
      let bVal = b[sdSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Date sort for last_active
      if (sdSortColumn === 'last_active') {
        const aDate = aVal ? new Date(aVal).getTime() : 0;
        const bDate = bVal ? new Date(bVal).getTime() : 0;
        const result = aDate - bDate;
        return sdSortOrder === 'asc' ? result : -result;
      }

      const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sdSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [sdData, sdSearch, sdFilters, sdSortColumn, sdSortOrder]);

  // System Devices Pagination
  const sdTotalFiltered = sdFiltered.length;
  const sdTotalPages = Math.max(1, Math.ceil(sdTotalFiltered / sdPerPage));
  const sdSafePage = Math.min(sdPage, sdTotalPages);

  const sdPaginated = useMemo(() => {
    const start = (sdSafePage - 1) * sdPerPage;
    return sdFiltered.slice(start, start + sdPerPage);
  }, [sdFiltered, sdSafePage, sdPerPage]);

  const sdShowingStart = sdTotalFiltered === 0 ? 0 : (sdSafePage - 1) * sdPerPage + 1;
  const sdShowingEnd = Math.min(sdSafePage * sdPerPage, sdTotalFiltered);

  useEffect(() => { setSdPage(1); }, [sdSearch, sdFilters, sdPerPage]);

  const sdPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, sdSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(sdTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [sdSafePage, sdTotalPages]);

  // ==========================================================================
  // SERVERS — Filter / Search / Sort Pipeline
  // ==========================================================================
  const svFiltered = useMemo(() => {
    let data = [...svData];

    // 1. Search
    if (svSearch.trim()) {
      const s = svSearch.toLowerCase();
      data = data.filter(
        (r) =>
          r.description?.toLowerCase().includes(s) ||
          r.palcare_interface_name?.toLowerCase().includes(s) ||
          r.area_name?.toLowerCase().includes(s) ||
          r.ip_address?.toLowerCase().includes(s) ||
          r.mac_addr?.toLowerCase().includes(s) ||
          r.software_version?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(svFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        if (columnKey === 'is_active') {
          return values.some((v) => {
            if (v === 'Active') return !!row.is_active;
            if (v === 'Backup') return !row.is_active;
            return String(row.is_active) === v;
          });
        }
        if (columnKey === 'is_offline') {
          return values.some((v) => {
            if (v === 'Online') return row.is_offline != null && !row.is_offline;
            if (v === 'Offline') return row.is_offline != null && !!row.is_offline;
            if (v === 'Unknown') return row.is_offline == null;
            return String(row.is_offline) === v;
          });
        }
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '');
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      const aVal = a[svSortColumn];
      const bVal = b[svSortColumn];

      // Date sort for last_heard
      if (svSortColumn === 'last_heard') {
        const aDate = aVal ? new Date(aVal).getTime() : 0;
        const bDate = bVal ? new Date(bVal).getTime() : 0;
        const result = aDate - bDate;
        return svSortOrder === 'asc' ? result : -result;
      }

      // Boolean columns — sort by display label
      if (svSortColumn === 'is_active') {
        const aLabel = aVal ? 'Active' : 'Backup';
        const bLabel = bVal ? 'Active' : 'Backup';
        const result = aLabel.localeCompare(bLabel);
        return svSortOrder === 'asc' ? result : -result;
      }
      if (svSortColumn === 'is_offline') {
        const aLabel = aVal != null && !!aVal ? 'Offline' : aVal != null && !aVal ? 'Online' : 'Unknown';
        const bLabel = bVal != null && !!bVal ? 'Offline' : bVal != null && !bVal ? 'Online' : 'Unknown';
        const result = aLabel.localeCompare(bLabel);
        return svSortOrder === 'asc' ? result : -result;
      }

      const aStr = aVal != null ? String(aVal) : '';
      const bStr = bVal != null ? String(bVal) : '';
      const result = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
      return svSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [svData, svSearch, svFilters, svSortColumn, svSortOrder]);

  // Servers Pagination
  const svTotalFiltered = svFiltered.length;
  const svTotalPages = Math.max(1, Math.ceil(svTotalFiltered / svPerPage));
  const svSafePage = Math.min(svPage, svTotalPages);

  const svPaginated = useMemo(() => {
    const start = (svSafePage - 1) * svPerPage;
    return svFiltered.slice(start, start + svPerPage);
  }, [svFiltered, svSafePage, svPerPage]);

  const svShowingStart = svTotalFiltered === 0 ? 0 : (svSafePage - 1) * svPerPage + 1;
  const svShowingEnd = Math.min(svSafePage * svPerPage, svTotalFiltered);

  useEffect(() => { setSvPage(1); }, [svSearch, svFilters, svPerPage]);

  const svPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, svSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(svTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [svSafePage, svTotalPages]);

  /** Column labels for servers (used by ActiveFilterBar) */
  const svColumnLabels = useMemo(() => {
    const map = {};
    svColumnDefs.forEach((c) => { map[c.key] = c.label; });
    return map;
  }, [svColumnDefs]);

  // ==========================================================================
  // NURSE CALL HANDLERS
  // ==========================================================================
  const handleNcSort = useCallback((column) => {
    if (ncSortColumn === column) {
      setNcSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setNcSortColumn(column);
      setNcSortOrder('asc');
    }
  }, [ncSortColumn]);

  const handleNcFilterChange = useCallback((columnKey, values) => {
    setNcFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleNcRemoveFilter = useCallback((columnKey, value) => {
    setNcFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleNcClearAllFilters = useCallback(() => {
    setNcFilters({});
    setNcSearch('');
  }, []);

  const handleNcSelectRow = useCallback((id) => {
    setNcSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleNcSelectAll = useCallback(() => {
    const allIds = ncPaginated.map((d) => d.device_id);
    const allSelected = allIds.every((id) => ncSelectedIds.includes(id));
    if (allSelected) {
      setNcSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setNcSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [ncPaginated, ncSelectedIds]);

  // Single device unassign (Nurse Call)
  const handleNcUnassignDevice = useCallback(async (device) => {
    if (!confirm(`Unassign "${device.device_name}"? This will deactivate it and remove it from its assigned area and resident.`)) return;
    const session = getSessionParams();
    const campusConfig = getCampusConfig();
    const result = await unassignDevices([device], session);
    if (result[0]) {
      loadNurseCallDevices(false);
      showToast('success', `${device.device_name} unassigned`);
    } else {
      showToast('error', result[1] || 'Failed to unassign device');
    }
  }, [getSessionParams, loadNurseCallDevices, showToast]);

  // Single device delete (Nurse Call)
  const handleNcDeleteDevice = useCallback(async (device) => {
    if (!confirm(`Delete "${device.device_name}"? This cannot be undone.`)) return;
    const session = getSessionParams();
    const result = await deleteDevices([device], session);
    if (result[0]) {
      setNcSelectedIds((prev) => prev.filter((id) => id !== device.device_id));
      loadNurseCallDevices(false);
      showToast('success', `${device.device_name} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [getSessionParams, loadNurseCallDevices, showToast]);

  // Bulk unassign (Nurse Call)
  const handleNcBulkUnassign = useCallback(async () => {
    const count = ncSelectedIds.length;
    if (!confirm(`Unassign ${count} device(s)? This will deactivate them and remove them from their assigned areas and residents.`)) return;
    const session = getSessionParams();
    const devices = ncData.filter((d) => ncSelectedIds.includes(d.device_id));
    const result = await unassignDevices(devices, session);
    if (result[0]) {
      setNcSelectedIds([]);
      loadNurseCallDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} unassigned`);
    } else {
      showToast('error', result[1] || 'Bulk unassign failed');
    }
  }, [ncSelectedIds, ncData, getSessionParams, loadNurseCallDevices, showToast]);

  // Bulk delete (Nurse Call)
  const handleNcBulkDelete = useCallback(async () => {
    const count = ncSelectedIds.length;
    if (!confirm(`Delete ${count} device(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const devices = ncData.filter((d) => ncSelectedIds.includes(d.device_id));
    const result = await deleteDevices(devices, session);
    if (result[0]) {
      setNcSelectedIds([]);
      loadNurseCallDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [ncSelectedIds, ncData, getSessionParams, loadNurseCallDevices, showToast]);

  // Export (Nurse Call)
  const handleNcExportAll = useCallback(() => {
    if (ncFiltered.length) exportDevicesCsv(ncFiltered, 'devices.csv', myRoleWeight, ncVisibleColumns);
  }, [ncFiltered, myRoleWeight, ncVisibleColumns]);

  const handleNcBulkExport = useCallback(() => {
    const selected = ncData.filter((d) => ncSelectedIds.includes(d.device_id));
    if (selected.length) exportDevicesCsv(selected, 'devices-selected.csv', myRoleWeight, ncVisibleColumns);
  }, [ncSelectedIds, ncData, myRoleWeight, ncVisibleColumns]);

  // Add device (Nurse Call). Supports multi-add: when called with `{ silent: true }`
  // the drawer is running a batch, so we suppress per-device toasts and skip the
  // close/reload — the drawer closes itself and we toast a rollup via handleNcBatchComplete.
  const handleNcAddDevice = useCallback(async (formData, transType, options = {}) => {
    const session = getSessionParams();
    const result = await addDevice(formData, transType, session);
    if (options.silent) {
      return result;
    }
    if (result[0]) {
      setNcDrawerOpen(false);
      loadNurseCallDevices(false);
      showToast('success', 'Device added successfully');
    } else {
      showToast('error', result[1] || 'Failed to add device');
    }
    return result;
  }, [getSessionParams, loadNurseCallDevices, showToast]);

  // Rollup toast + reload after a batch add (multi-mode)
  const handleNcBatchComplete = useCallback(({ added, failed, total }) => {
    if (added > 0) loadNurseCallDevices(false);
    if (failed === 0) {
      showToast('success', `${added} device${added === 1 ? '' : 's'} added successfully`);
    } else if (added === 0) {
      showToast('error', `Failed to add ${failed} of ${total} device${total === 1 ? '' : 's'}`);
    } else {
      showToast('error', `Added ${added} of ${total} devices — ${failed} failed`);
    }
  }, [loadNurseCallDevices, showToast]);

  // ==========================================================================
  // WANDERING HANDLERS
  // ==========================================================================
  const handleWdSort = useCallback((column) => {
    if (wdSortColumn === column) {
      setWdSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setWdSortColumn(column);
      setWdSortOrder('asc');
    }
  }, [wdSortColumn]);

  const handleWdFilterChange = useCallback((columnKey, values) => {
    setWdFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleWdRemoveFilter = useCallback((columnKey, value) => {
    setWdFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleWdClearAllFilters = useCallback(() => {
    setWdFilters({});
    setWdSearch('');
  }, []);

  const handleWdSelectRow = useCallback((id) => {
    setWdSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleWdSelectAll = useCallback(() => {
    const allIds = wdPaginated.map((d) => d.device_id);
    const allSelected = allIds.every((id) => wdSelectedIds.includes(id));
    if (allSelected) {
      setWdSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setWdSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [wdPaginated, wdSelectedIds]);

  // Single device unassign (Wandering)
  const handleWdUnassignDevice = useCallback(async (device) => {
    if (!confirm(`Unassign "${device.device_name}"? This will deactivate it and remove it from its assigned area and resident.`)) return;
    const session = getSessionParams();
    const result = await unassignDevices([device], session);
    if (result[0]) {
      loadWanderingDevices(false);
      showToast('success', `${device.device_name} unassigned`);
    } else {
      showToast('error', result[1] || 'Failed to unassign device');
    }
  }, [getSessionParams, loadWanderingDevices, showToast]);

  // Single device delete (Wandering)
  const handleWdDeleteDevice = useCallback(async (device) => {
    if (!confirm(`Delete "${device.device_name}"? This cannot be undone.`)) return;
    const session = getSessionParams();
    const result = await deleteDevices([device], session);
    if (result[0]) {
      setWdSelectedIds((prev) => prev.filter((id) => id !== device.device_id));
      loadWanderingDevices(false);
      showToast('success', `${device.device_name} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [getSessionParams, loadWanderingDevices, showToast]);

  // Bulk unassign (Wandering)
  const handleWdBulkUnassign = useCallback(async () => {
    const count = wdSelectedIds.length;
    if (!confirm(`Unassign ${count} device(s)? This will deactivate them and remove them from their assigned areas and residents.`)) return;
    const session = getSessionParams();
    const devices = wdData.filter((d) => wdSelectedIds.includes(d.device_id));
    const result = await unassignDevices(devices, session);
    if (result[0]) {
      setWdSelectedIds([]);
      loadWanderingDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} unassigned`);
    } else {
      showToast('error', result[1] || 'Bulk unassign failed');
    }
  }, [wdSelectedIds, wdData, getSessionParams, loadWanderingDevices, showToast]);

  // Bulk delete (Wandering)
  const handleWdBulkDelete = useCallback(async () => {
    const count = wdSelectedIds.length;
    if (!confirm(`Delete ${count} device(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const devices = wdData.filter((d) => wdSelectedIds.includes(d.device_id));
    const result = await deleteDevices(devices, session);
    if (result[0]) {
      setWdSelectedIds([]);
      loadWanderingDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [wdSelectedIds, wdData, getSessionParams, loadWanderingDevices, showToast]);

  // Export (Wandering)
  const handleWdExportAll = useCallback(() => {
    if (wdFiltered.length) exportWanderingDevicesCsv(wdFiltered, 'wandering-devices.csv', myRoleWeight, wdVisibleColumns);
  }, [wdFiltered, myRoleWeight, wdVisibleColumns]);

  const handleWdBulkExport = useCallback(() => {
    const selected = wdData.filter((d) => wdSelectedIds.includes(d.device_id));
    if (selected.length) exportWanderingDevicesCsv(selected, 'wandering-devices-selected.csv', myRoleWeight, wdVisibleColumns);
  }, [wdSelectedIds, wdData, myRoleWeight, wdVisibleColumns]);

  // Add device (Wandering) — always uses device_add_resident
  const handleWdAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'device_add_resident', session);
    if (result[0]) {
      setWdDrawerOpen(false);
      loadWanderingDevices(false);
      showToast('success', 'Wandering device added successfully');
    } else {
      showToast('error', result[1] || 'Failed to add wandering device');
    }
  }, [getSessionParams, loadWanderingDevices, showToast]);

  // ==========================================================================
  // SPEAKER DEVICES HANDLERS
  // ==========================================================================
  const handleSpSort = useCallback((column) => {
    if (spSortColumn === column) {
      setSpSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSpSortColumn(column);
      setSpSortOrder('asc');
    }
  }, [spSortColumn]);

  const handleSpFilterChange = useCallback((columnKey, values) => {
    setSpFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleSpRemoveFilter = useCallback((columnKey, value) => {
    setSpFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleSpClearAllFilters = useCallback(() => {
    setSpFilters({});
    setSpSearch('');
  }, []);

  const handleSpSelectRow = useCallback((id) => {
    setSpSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleSpSelectAll = useCallback(() => {
    const allIds = spPaginated.map((d) => d.device_id);
    const allSelected = allIds.every((id) => spSelectedIds.includes(id));
    if (allSelected) {
      setSpSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSpSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [spPaginated, spSelectedIds]);

  // Single device unassign (Speaker)
  const handleSpUnassignDevice = useCallback(async (device) => {
    if (!confirm(`Unassign "${device.device_name}"? This will deactivate it and remove it from its assigned area and resident.`)) return;
    const session = getSessionParams();
    const campusConfig = getCampusConfig();
    const result = await unassignDevices([device], session, campusConfig?.is_grandstream);
    if (result[0]) {
      loadSpeakerDevices(false);
      showToast('success', `${device.device_name} unassigned`);
    } else {
      showToast('error', result[1] || 'Failed to unassign device');
    }
  }, [getSessionParams, loadSpeakerDevices, showToast]);

  // Single device delete (Speaker)
  const handleSpDeleteDevice = useCallback(async (device) => {
    if (!confirm(`Delete "${device.device_name}"? This cannot be undone.`)) return;
    const session = getSessionParams();
    const campusConfig = getCampusConfig();
    const result = await deleteDevices([device], session, campusConfig?.is_grandstream);
    if (result[0]) {
      setSpSelectedIds((prev) => prev.filter((id) => id !== device.device_id));
      loadSpeakerDevices(false);
      showToast('success', `${device.device_name} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [getSessionParams, loadSpeakerDevices, showToast]);

  // Bulk unassign (Speaker)
  const handleSpBulkUnassign = useCallback(async () => {
    const count = spSelectedIds.length;
    if (!confirm(`Unassign ${count} device(s)? This will deactivate them and remove them from their assigned areas and residents.`)) return;
    const session = getSessionParams();
    const campusConfig = getCampusConfig();
    const devices = spData.filter((d) => spSelectedIds.includes(d.device_id));
    const result = await unassignDevices(devices, session, campusConfig?.is_grandstream);
    if (result[0]) {
      setSpSelectedIds([]);
      loadSpeakerDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} unassigned`);
    } else {
      showToast('error', result[1] || 'Bulk unassign failed');
    }
  }, [spSelectedIds, spData, getSessionParams, loadSpeakerDevices, showToast]);

  // Bulk delete (Speaker)
  const handleSpBulkDelete = useCallback(async () => {
    const count = spSelectedIds.length;
    if (!confirm(`Delete ${count} device(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const campusConfig = getCampusConfig();
    const devices = spData.filter((d) => spSelectedIds.includes(d.device_id));
    const result = await deleteDevices(devices, session, campusConfig?.is_grandstream);
    if (result[0]) {
      setSpSelectedIds([]);
      loadSpeakerDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [spSelectedIds, spData, getSessionParams, loadSpeakerDevices, showToast]);

  // Export (Speaker)
  const handleSpExportAll = useCallback(() => {
    if (spFiltered.length) exportSpeakerDevicesCsv(spFiltered, 'speaker-devices.csv', myRoleWeight, spVisibleColumns);
  }, [spFiltered, myRoleWeight, spVisibleColumns]);

  const handleSpBulkExport = useCallback(() => {
    const selected = spData.filter((d) => spSelectedIds.includes(d.device_id));
    if (selected.length) exportSpeakerDevicesCsv(selected, 'speaker-devices-selected.csv', myRoleWeight, spVisibleColumns);
  }, [spSelectedIds, spData, myRoleWeight, spVisibleColumns]);

  // Add device (Speaker)
  const handleSpAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addSpeakerDevice(formData, session);
    if (result[0]) {
      setSpDrawerOpen(false);
      loadSpeakerDevices(false);
      showToast('success', 'Speaker device added successfully');
    } else {
      showToast('error', result[1] || 'Failed to add speaker device');
    }
  }, [getSessionParams, loadSpeakerDevices, showToast]);

  // ==========================================================================
  // SYSTEM DEVICES HANDLERS
  // ==========================================================================
  const handleSdSort = useCallback((column) => {
    if (sdSortColumn === column) {
      setSdSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSdSortColumn(column);
      setSdSortOrder('asc');
    }
  }, [sdSortColumn]);

  const handleSdFilterChange = useCallback((columnKey, values) => {
    setSdFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleSdRemoveFilter = useCallback((columnKey, value) => {
    setSdFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleSdClearAllFilters = useCallback(() => {
    setSdFilters({});
    setSdSearch('');
  }, []);

  const handleSdSelectRow = useCallback((id) => {
    setSdSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleSdSelectAll = useCallback(() => {
    const allIds = sdPaginated.map((d) => d.device_id);
    const allSelected = allIds.every((id) => sdSelectedIds.includes(id));
    if (allSelected) {
      setSdSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSdSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [sdPaginated, sdSelectedIds]);

  // Single device delete (System — no unassign)
  const handleSdDeleteDevice = useCallback(async (device) => {
    if (!confirm(`Delete "${device.device_name}"? This cannot be undone.`)) return;
    const session = getSessionParams();
    const result = await deleteSystemDevices([device], session);
    if (result[0]) {
      setSdSelectedIds((prev) => prev.filter((id) => id !== device.device_id));
      loadSystemDevices(false);
      showToast('success', `${device.device_name} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [getSessionParams, loadSystemDevices, showToast]);

  // Bulk delete (System — no unassign)
  const handleSdBulkDelete = useCallback(async () => {
    const count = sdSelectedIds.length;
    if (!confirm(`Delete ${count} device(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const devices = sdData.filter((d) => sdSelectedIds.includes(d.device_id));
    const result = await deleteSystemDevices(devices, session);
    if (result[0]) {
      setSdSelectedIds([]);
      loadSystemDevices(false);
      showToast('success', `${count} device${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [sdSelectedIds, sdData, getSessionParams, loadSystemDevices, showToast]);

  // Export (System)
  const handleSdExportAll = useCallback(() => {
    if (sdFiltered.length) exportSystemDevicesCsv(sdFiltered, 'system-devices.csv', myRoleWeight, sdVisibleColumns);
  }, [sdFiltered, myRoleWeight, sdVisibleColumns]);

  const handleSdBulkExport = useCallback(() => {
    const selected = sdData.filter((d) => sdSelectedIds.includes(d.device_id));
    if (selected.length) exportSystemDevicesCsv(selected, 'system-devices-selected.csv', myRoleWeight, sdVisibleColumns);
  }, [sdSelectedIds, sdData, myRoleWeight, sdVisibleColumns]);

  // Add device (System Devices) — always uses system_device_add
  const handleSdAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'system_device_add', session);
    if (result[0]) {
      setSdDrawerOpen(false);
      loadSystemDevices(false);
      showToast('success', 'System device added successfully');
    } else {
      showToast('error', result[1] || 'Failed to add system device');
    }
  }, [getSessionParams, loadSystemDevices, showToast]);

  // ==========================================================================
  // SERVER HANDLERS
  // ==========================================================================
  const handleSvSort = useCallback((column) => {
    if (svSortColumn === column) {
      setSvSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSvSortColumn(column);
      setSvSortOrder('asc');
    }
  }, [svSortColumn]);

  const handleSvFilterChange = useCallback((columnKey, values) => {
    setSvFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleSvRemoveFilter = useCallback((columnKey, value) => {
    setSvFilters((prev) => {
      const updated = { ...prev };
      if (updated[columnKey]) {
        updated[columnKey] = updated[columnKey].filter((v) => v !== value);
        if (updated[columnKey].length === 0) delete updated[columnKey];
      }
      return updated;
    });
  }, []);

  const handleSvClearAllFilters = useCallback(() => {
    setSvFilters({});
  }, []);

  const handleSvSelectRow = useCallback((id) => {
    setSvSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSvSelectAll = useCallback(() => {
    const allIds = svPaginated.map((r) => r.palcare_device_id);
    const allSelected = allIds.every((id) => svSelectedIds.includes(id));
    if (allSelected) {
      setSvSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSvSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [svPaginated, svSelectedIds]);

  // Single delete
  const handleSvDeleteDevice = useCallback(async (server) => {
    if (!confirm(`Delete "${server.description}"? This cannot be undone.`)) return;
    const session = getSessionParams();
    const result = await deleteServers([server], session);
    if (result[0]) {
      setSvSelectedIds((prev) => prev.filter((id) => id !== server.palcare_device_id));
      loadServers(false);
      showToast('success', `${server.description} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [getSessionParams, loadServers, showToast]);

  // Bulk delete
  const handleSvBulkDelete = useCallback(async () => {
    const count = svSelectedIds.length;
    if (!confirm(`Delete ${count} server(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const servers = svData.filter((s) => svSelectedIds.includes(s.palcare_device_id));
    const result = await deleteServers(servers, session);
    if (result[0]) {
      setSvSelectedIds([]);
      loadServers(false);
      showToast('success', `${count} server${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [svSelectedIds, svData, getSessionParams, loadServers, showToast]);

  // Add server
  const handleSvAddServer = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addServer(formData, session);
    if (result[0]) {
      setSvDrawerOpen(false);
      loadServers(false);
      showToast('success', 'Server added successfully');
    } else {
      showToast('error', result[1] || 'Failed to add server');
    }
  }, [getSessionParams, loadServers, showToast]);

  // Export
  const handleSvExportAll = useCallback(() => {
    if (svFiltered.length) exportServersCsv(svFiltered, 'servers.csv', myRoleWeight, svVisibleColumns);
  }, [svFiltered, myRoleWeight, svVisibleColumns]);

  const handleSvBulkExport = useCallback(() => {
    const selected = svData.filter((s) => svSelectedIds.includes(s.palcare_device_id));
    if (selected.length) exportServersCsv(selected, 'servers-selected.csv', myRoleWeight, svVisibleColumns);
  }, [svSelectedIds, svData, myRoleWeight, svVisibleColumns]);

  // ==========================================================================
  // COLUMN LABEL MAPS (for ActiveFilterBar)
  // ==========================================================================
  const ncColumnLabels = useMemo(
    () => Object.fromEntries(ncColumnDefs.map((c) => [c.filterKey || c.key, c.label])),
    [ncColumnDefs]
  );

  const wdColumnLabels = useMemo(
    () => Object.fromEntries(WANDERING_COLUMN_DEFS.map((c) => [c.filterKey || c.key, c.label])),
    []
  );

  const spColumnLabels = useMemo(
    () => Object.fromEntries(SPEAKER_COLUMN_DEFS.map((c) => [c.filterKey || c.key, c.label])),
    []
  );

  const sdColumnLabels = useMemo(
    () => Object.fromEntries(SYSTEM_COLUMN_DEFS.map((c) => [c.filterKey || c.key, c.label])),
    []
  );

  // Campus name — derive from campusList (which correctly maps session.campusData names)
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // ==========================================================================
  // RENDER HELPERS
  // ==========================================================================

  // Nurse Call Bulk Bar
  const renderNcBulkBar = () => {
    if (ncSelectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {ncSelectedIds.length} device{ncSelectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setNcSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleNcBulkExport} className="dp-bulk-btn" aria-label="Export selected devices">
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {editRights ? (
            <button type="button" onClick={handleNcBulkUnassign} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Unassign selected devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              Unassign
            </button>
          ) : null}
          {deleteRights ? (
            <button type="button" onClick={handleNcBulkDelete} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Delete selected devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // Wandering Bulk Bar
  const renderWdBulkBar = () => {
    if (wdSelectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {wdSelectedIds.length} device{wdSelectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setWdSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleWdBulkExport} className="dp-bulk-btn" aria-label="Export selected wandering devices">
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {editRights ? (
            <button type="button" onClick={handleWdBulkUnassign} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Unassign selected wandering devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              Unassign
            </button>
          ) : null}
          {deleteRights ? (
            <button type="button" onClick={handleWdBulkDelete} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Delete selected wandering devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // Speaker Devices Bulk Bar
  const renderSpBulkBar = () => {
    if (spSelectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {spSelectedIds.length} device{spSelectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setSpSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleSpBulkExport} className="dp-bulk-btn" aria-label="Export selected speaker devices">
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {restrictedEditRights ? (
            <button type="button" onClick={handleSpBulkUnassign} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Unassign selected speaker devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              Unassign
            </button>
          ) : null}
          {deleteRights ? (
            <button type="button" onClick={handleSpBulkDelete} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Delete selected speaker devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // System Devices Bulk Bar (delete only, no unassign)
  const renderSdBulkBar = () => {
    if (sdSelectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {sdSelectedIds.length} device{sdSelectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setSdSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleSdBulkExport} className="dp-bulk-btn" aria-label="Export selected system devices">
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {deleteRights ? (
            <button type="button" onClick={handleSdBulkDelete} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Delete selected system devices">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderSvBulkBar = () => {
    if (svSelectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {svSelectedIds.length} server{svSelectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setSvSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleSvBulkExport} className="dp-bulk-btn" aria-label="Export selected servers">
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          {deleteRights ? (
            <button type="button" onClick={handleSvBulkDelete} className="dp-bulk-btn dp-bulk-btn--danger" aria-label="Delete selected servers">
              <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="dp-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`dp-toast ${toast.type === 'success' ? 'dp-toast--success' : 'dp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="dp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="dp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="dp-page-header">
        <div className="dp-page-header__inner">
          <div className="dp-breadcrumb-row">
            <div className="dp-breadcrumb">
              <span className="dp-breadcrumb__text">Community</span>
              <svg className="dp-icon-md dp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="dp-breadcrumb__current">Devices</span>
            </div>
            <div className="dp-campus-row">
              <label htmlFor="dp-campus-selector" className="dp-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="dp-campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="dp-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="dp-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="dp-page-title">Devices</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="dp-tab-bar">
        <div className="dp-tab-bar__inner">
          <nav className="dp-tab-nav" aria-label="Device type tabs">
            <Link to="/community/devices" className={`dp-tab ${activeTab === 'nursecall' ? 'is-active' : ''}`}>
              Nurse Call ({ncData.length})
              {activeTab === 'nursecall' ? <span className="dp-tab__indicator" /> : null}
            </Link>
            <Link to="/community/wander_devices" className={`dp-tab ${activeTab === 'wandering' ? 'is-active' : ''}`}>
              Wandering ({wdData.length})
              {activeTab === 'wandering' ? <span className="dp-tab__indicator" /> : null}
            </Link>
            {iAmHigherAuth(10, myRoleWeight) && isGrandstream ? (
              <Link to="/community/speaker_devices" className={`dp-tab ${activeTab === 'speakers' ? 'is-active' : ''}`}>
                Speaker Devices ({spData.length})
                {activeTab === 'speakers' ? <span className="dp-tab__indicator" /> : null}
              </Link>
            ) : null}
            {iAmHigherAuth(10, myRoleWeight) ? (
              <Link to="/community/system_devices" className={`dp-tab ${activeTab === 'system' ? 'is-active' : ''}`}>
                System Devices ({sdData.length})
                {activeTab === 'system' ? <span className="dp-tab__indicator" /> : null}
              </Link>
            ) : null}
            {iAmHigherAuth(10, myRoleWeight) ? (
              <Link to="/community/server_devices" className={`dp-tab ${activeTab === 'servers' ? 'is-active' : ''}`}>
                Servers ({svData.length})
                {activeTab === 'servers' ? <span className="dp-tab__indicator" /> : null}
              </Link>
            ) : null}
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="dp-content">

        {/* ============================================================
            NURSE CALL TAB
            ============================================================ */}
        {activeTab === 'nursecall' ? (
          <>
            <div className="dp-controls">
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search devices..."
                    value={ncSearch}
                    onChange={(e) => setNcSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search nurse call devices"
                  />
                </div>
                <div className="dp-toggle-group">
                  <button
                    type="button"
                    onClick={() => setNcShowActiveOnly(true)}
                    className={`dp-toggle-btn ${ncShowActiveOnly ? 'is-active' : ''}`}
                  >
                    Active Only ({ncData.filter((d) => !!d.is_active).length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setNcShowActiveOnly(false)}
                    className={`dp-toggle-btn ${!ncShowActiveOnly ? 'is-active' : ''}`}
                  >
                    All ({ncData.length})
                  </button>
                </div>
              </div>
              <div className="dp-controls__right">
                <ColumnVisibilityToggle columns={ncColumnDefs} visibleColumns={ncVisibleColumns} onToggle={handleNcColumnToggle} />
                <button type="button" className="dp-btn dp-btn--secondary" onClick={handleNcExportAll} aria-label="Export nurse call devices">
                  <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {addRights ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--primary"
                    onClick={() => setNcDrawerOpen(true)}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Device
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={ncFilters} columnLabels={ncColumnLabels} onRemoveFilter={handleNcRemoveFilter} onClearAll={handleNcClearAllFilters} />
            {renderNcBulkBar()}
            {ncLoading ? (
              <TableSkeleton />
            ) : (
              <DevicesTable
                data={ncPaginated}
                allData={ncData}
                visibleColumns={ncVisibleColumns}
                sortColumn={ncSortColumn}
                sortOrder={ncSortOrder}
                onSort={handleNcSort}
                filters={ncFilters}
                onFilterChange={handleNcFilterChange}
                selectedIds={ncSelectedIds}
                onSelectRow={handleNcSelectRow}
                onSelectAll={handleNcSelectAll}
                onUnassignDevice={handleNcUnassignDevice}
                onDeleteDevice={handleNcDeleteDevice}
                editRights={editRights}
                deleteRights={deleteRights}
                columnDefs={ncColumnDefs}
                tzOffset={tzOffset}
              />
            )}
            {/* Nurse Call Pagination */}
            <div className="dp-pagination">
              <p className="dp-pagination__info">
                Showing <span className="dp-pagination__emphasis">{ncShowingStart}</span>&ndash;<span className="dp-pagination__emphasis">{ncShowingEnd}</span> of{' '}
                <span className="dp-pagination__emphasis">{ncTotalFiltered}</span> devices
              </p>
              <div className="dp-pagination__controls">
                <div className="dp-per-page">
                  <label htmlFor="dp-nc-per-page" className="dp-per-page__label">Rows per page:</label>
                  <select
                    id="dp-nc-per-page"
                    value={ncPerPage}
                    onChange={(e) => setNcPerPage(parseInt(e.target.value, 10))}
                    className="dp-per-page__select"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                <nav className="dp-page-nav" aria-label="Nurse call devices pagination">
                  <button
                    type="button"
                    onClick={() => setNcPage((p) => Math.max(1, p - 1))}
                    disabled={ncSafePage <= 1}
                    className="dp-page-btn"
                    aria-label="Previous page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  {ncPageNumbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setNcPage(num)}
                      className={`dp-page-num ${num === ncSafePage ? 'is-active' : ''}`}
                      aria-label={`Page ${num}`}
                      aria-current={num === ncSafePage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNcPage((p) => Math.min(ncTotalPages, p + 1))}
                    disabled={ncSafePage >= ncTotalPages}
                    className="dp-page-btn"
                    aria-label="Next page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>

            {/* Add Device Drawer */}
            <AddDeviceDrawer
              isOpen={ncDrawerOpen}
              onClose={() => setNcDrawerOpen(false)}
              onSave={handleNcAddDevice}
              onBatchComplete={handleNcBatchComplete}
              existingDevices={ncData}
              campusId={campusId}
              manufacturers={[{ id: 1, label: 'Inovonics' }, { id: 4, label: 'PalCare' }]}
              sessionId={getSessionId()}
              serverVersion={getSession()?.server_version || ''}
              roleWeight={myRoleWeight}
              allowMultiple
            />
          </>
        ) : null}

        {/* ============================================================
            WANDERING TAB
            ============================================================ */}
        {activeTab === 'wandering' ? (
          <>
            <div className="dp-controls">
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search wandering devices..."
                    value={wdSearch}
                    onChange={(e) => setWdSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search wandering devices"
                  />
                </div>
                <div className="dp-toggle-group">
                  <button
                    type="button"
                    onClick={() => setWdShowActiveOnly(true)}
                    className={`dp-toggle-btn ${wdShowActiveOnly ? 'is-active' : ''}`}
                  >
                    Active Only ({wdData.filter((d) => !!d.is_active).length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setWdShowActiveOnly(false)}
                    className={`dp-toggle-btn ${!wdShowActiveOnly ? 'is-active' : ''}`}
                  >
                    All ({wdData.length})
                  </button>
                </div>
              </div>
              <div className="dp-controls__right">
                <ColumnVisibilityToggle columns={WANDERING_COLUMN_DEFS} visibleColumns={wdVisibleColumns} onToggle={handleWdColumnToggle} />
                <button type="button" className="dp-btn dp-btn--secondary" onClick={handleWdExportAll} aria-label="Export wandering devices">
                  <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {addRights ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--primary"
                    onClick={() => setWdDrawerOpen(true)}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Tag
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={wdFilters} columnLabels={wdColumnLabels} onRemoveFilter={handleWdRemoveFilter} onClearAll={handleWdClearAllFilters} />
            {renderWdBulkBar()}
            {wdLoading ? (
              <TableSkeleton />
            ) : (
              <WanderingDevicesTable
                data={wdPaginated}
                allData={wdData}
                visibleColumns={wdVisibleColumns}
                sortColumn={wdSortColumn}
                sortOrder={wdSortOrder}
                onSort={handleWdSort}
                filters={wdFilters}
                onFilterChange={handleWdFilterChange}
                selectedIds={wdSelectedIds}
                onSelectRow={handleWdSelectRow}
                onSelectAll={handleWdSelectAll}
                onUnassignDevice={handleWdUnassignDevice}
                onDeleteDevice={handleWdDeleteDevice}
                editRights={editRights}
                deleteRights={deleteRights}
              />
            )}
            {/* Wandering Pagination */}
            <div className="dp-pagination">
              <p className="dp-pagination__info">
                Showing <span className="dp-pagination__emphasis">{wdShowingStart}</span>&ndash;<span className="dp-pagination__emphasis">{wdShowingEnd}</span> of{' '}
                <span className="dp-pagination__emphasis">{wdTotalFiltered}</span> devices
              </p>
              <div className="dp-pagination__controls">
                <div className="dp-per-page">
                  <label htmlFor="dp-wd-per-page" className="dp-per-page__label">Rows per page:</label>
                  <select
                    id="dp-wd-per-page"
                    value={wdPerPage}
                    onChange={(e) => setWdPerPage(parseInt(e.target.value, 10))}
                    className="dp-per-page__select"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                <nav className="dp-page-nav" aria-label="Wandering devices pagination">
                  <button
                    type="button"
                    onClick={() => setWdPage((p) => Math.max(1, p - 1))}
                    disabled={wdSafePage <= 1}
                    className="dp-page-btn"
                    aria-label="Previous page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  {wdPageNumbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setWdPage(num)}
                      className={`dp-page-num ${num === wdSafePage ? 'is-active' : ''}`}
                      aria-label={`Page ${num}`}
                      aria-current={num === wdSafePage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setWdPage((p) => Math.min(wdTotalPages, p + 1))}
                    disabled={wdSafePage >= wdTotalPages}
                    className="dp-page-btn"
                    aria-label="Next page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
            {/* Add Wandering Device Drawer */}
            <AddWanderingDeviceDrawer
              isOpen={wdDrawerOpen}
              onClose={() => setWdDrawerOpen(false)}
              onSave={handleWdAddDevice}
              existingDevices={wdData}
              campusId={campusId}
            />
          </>
        ) : null}

        {/* ============================================================
            SPEAKER DEVICES TAB
            ============================================================ */}
        {activeTab === 'speakers' ? (
          <>
            <div className="dp-controls">
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search speaker devices..."
                    value={spSearch}
                    onChange={(e) => setSpSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search speaker devices"
                  />
                </div>
                <div className="dp-toggle-group">
                  <button
                    type="button"
                    onClick={() => setSpShowActiveOnly(true)}
                    className={`dp-toggle-btn ${spShowActiveOnly ? 'is-active' : ''}`}
                  >
                    Active Only ({spData.filter((d) => !!d.is_active).length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpShowActiveOnly(false)}
                    className={`dp-toggle-btn ${!spShowActiveOnly ? 'is-active' : ''}`}
                  >
                    All ({spData.length})
                  </button>
                </div>
              </div>
              <div className="dp-controls__right">
                <ColumnVisibilityToggle columns={SPEAKER_COLUMN_DEFS} visibleColumns={spVisibleColumns} onToggle={handleSpColumnToggle} />
                <button type="button" className="dp-btn dp-btn--secondary" onClick={handleSpExportAll} aria-label="Export speaker devices">
                  <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {restrictedEditRights ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--primary"
                    onClick={() => setSpDrawerOpen(true)}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Device
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={spFilters} columnLabels={spColumnLabels} onRemoveFilter={handleSpRemoveFilter} onClearAll={handleSpClearAllFilters} />
            {renderSpBulkBar()}
            {spLoading ? (
              <TableSkeleton />
            ) : (
              <SpeakerDevicesTable
                data={spPaginated}
                allData={spData}
                visibleColumns={spVisibleColumns}
                sortColumn={spSortColumn}
                sortOrder={spSortOrder}
                onSort={handleSpSort}
                filters={spFilters}
                onFilterChange={handleSpFilterChange}
                selectedIds={spSelectedIds}
                onSelectRow={handleSpSelectRow}
                onSelectAll={handleSpSelectAll}
                onUnassignDevice={handleSpUnassignDevice}
                onDeleteDevice={handleSpDeleteDevice}
                editRights={restrictedEditRights}
                deleteRights={deleteRights}
              />
            )}
            {/* Speaker Pagination */}
            <div className="dp-pagination">
              <p className="dp-pagination__info">
                Showing <span className="dp-pagination__emphasis">{spShowingStart}</span>&ndash;<span className="dp-pagination__emphasis">{spShowingEnd}</span> of{' '}
                <span className="dp-pagination__emphasis">{spTotalFiltered}</span> devices
              </p>
              <div className="dp-pagination__controls">
                <div className="dp-per-page">
                  <label htmlFor="dp-sp-per-page" className="dp-per-page__label">Rows per page:</label>
                  <select
                    id="dp-sp-per-page"
                    value={spPerPage}
                    onChange={(e) => setSpPerPage(parseInt(e.target.value, 10))}
                    className="dp-per-page__select"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                <nav className="dp-page-nav" aria-label="Speaker devices pagination">
                  <button
                    type="button"
                    onClick={() => setSpPage((p) => Math.max(1, p - 1))}
                    disabled={spSafePage <= 1}
                    className="dp-page-btn"
                    aria-label="Previous page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  {spPageNumbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setSpPage(num)}
                      className={`dp-page-num ${num === spSafePage ? 'is-active' : ''}`}
                      aria-label={`Page ${num}`}
                      aria-current={num === spSafePage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSpPage((p) => Math.min(spTotalPages, p + 1))}
                    disabled={spSafePage >= spTotalPages}
                    className="dp-page-btn"
                    aria-label="Next page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
            {/* Add Speaker Device Drawer */}
            <AddSpeakerDeviceDrawer
              isOpen={spDrawerOpen}
              onClose={() => setSpDrawerOpen(false)}
              onSave={handleSpAddDevice}
              existingDevices={spData}
              campusId={campusId}
              deviceTypes={spDeviceTypes}
              models={spModels}
            />
          </>
        ) : null}

        {/* ============================================================
            SYSTEM DEVICES TAB
            ============================================================ */}
        {activeTab === 'system' ? (
          <>
            <div className="dp-controls">
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search system devices..."
                    value={sdSearch}
                    onChange={(e) => setSdSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search system devices"
                  />
                </div>
              </div>
              <div className="dp-controls__right">
                <ColumnVisibilityToggle columns={SYSTEM_COLUMN_DEFS} visibleColumns={sdVisibleColumns} onToggle={handleSdColumnToggle} />
                <button type="button" className="dp-btn dp-btn--secondary" onClick={handleSdExportAll} aria-label="Export system devices">
                  <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {restrictedEditRights ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--primary"
                    onClick={() => setSdDrawerOpen(true)}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Device
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={sdFilters} columnLabels={sdColumnLabels} onRemoveFilter={handleSdRemoveFilter} onClearAll={handleSdClearAllFilters} />
            {renderSdBulkBar()}
            {sdLoading ? (
              <TableSkeleton />
            ) : (
              <SystemDevicesTable
                data={sdPaginated}
                allData={sdData}
                visibleColumns={sdVisibleColumns}
                sortColumn={sdSortColumn}
                sortOrder={sdSortOrder}
                onSort={handleSdSort}
                filters={sdFilters}
                onFilterChange={handleSdFilterChange}
                selectedIds={sdSelectedIds}
                onSelectRow={handleSdSelectRow}
                onSelectAll={handleSdSelectAll}
                onDeleteDevice={handleSdDeleteDevice}
                editRights={restrictedEditRights}
                deleteRights={deleteRights}
                tzOffset={tzOffset}
              />
            )}
            {/* System Devices Pagination */}
            <div className="dp-pagination">
              <p className="dp-pagination__info">
                Showing <span className="dp-pagination__emphasis">{sdShowingStart}</span>&ndash;<span className="dp-pagination__emphasis">{sdShowingEnd}</span> of{' '}
                <span className="dp-pagination__emphasis">{sdTotalFiltered}</span> devices
              </p>
              <div className="dp-pagination__controls">
                <div className="dp-per-page">
                  <label htmlFor="dp-sd-per-page" className="dp-per-page__label">Rows per page:</label>
                  <select
                    id="dp-sd-per-page"
                    value={sdPerPage}
                    onChange={(e) => setSdPerPage(parseInt(e.target.value, 10))}
                    className="dp-per-page__select"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                <nav className="dp-page-nav" aria-label="System devices pagination">
                  <button
                    type="button"
                    onClick={() => setSdPage((p) => Math.max(1, p - 1))}
                    disabled={sdSafePage <= 1}
                    className="dp-page-btn"
                    aria-label="Previous page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  {sdPageNumbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setSdPage(num)}
                      className={`dp-page-num ${num === sdSafePage ? 'is-active' : ''}`}
                      aria-label={`Page ${num}`}
                      aria-current={num === sdSafePage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSdPage((p) => Math.min(sdTotalPages, p + 1))}
                    disabled={sdSafePage >= sdTotalPages}
                    className="dp-page-btn"
                    aria-label="Next page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>

            {/* Add System Device Drawer */}
            <AddDeviceDrawer
              isOpen={sdDrawerOpen}
              onClose={() => setSdDrawerOpen(false)}
              onSave={handleSdAddDevice}
              existingDevices={sdData}
              campusId={campusId}
              categoryConfig={sdCategoryConfig}
              title="Add System Device"
            />
          </>
        ) : null}

        {/* ============================================================
            SERVERS TAB
            ============================================================ */}
        {activeTab === 'servers' ? (
          <>
            <div className="dp-controls">
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search servers..."
                    value={svSearch}
                    onChange={(e) => setSvSearch(e.target.value)}
                    className="dp-search__input"
                    aria-label="Search servers"
                  />
                </div>
              </div>
              <div className="dp-controls__right">
                <ColumnVisibilityToggle columns={svColumnDefs} visibleColumns={svVisibleColumns} onToggle={handleSvColumnToggle} />
                <button type="button" className="dp-btn dp-btn--secondary" onClick={handleSvExportAll} aria-label="Export servers">
                  <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                {restrictedEditRights ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--primary"
                    onClick={() => setSvDrawerOpen(true)}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Server
                  </button>
                ) : null}
              </div>
            </div>
            <ActiveFilterBar filters={svFilters} columnLabels={svColumnLabels} onRemoveFilter={handleSvRemoveFilter} onClearAll={handleSvClearAllFilters} />
            {renderSvBulkBar()}
            {svLoading ? (
              <TableSkeleton />
            ) : (
              <ServersTable
                data={svPaginated}
                allData={svData}
                visibleColumns={svVisibleColumns}
                columnDefs={svColumnDefs}
                sortColumn={svSortColumn}
                sortOrder={svSortOrder}
                onSort={handleSvSort}
                filters={svFilters}
                onFilterChange={handleSvFilterChange}
                selectedIds={svSelectedIds}
                onSelectRow={handleSvSelectRow}
                onSelectAll={handleSvSelectAll}
                onDeleteDevice={handleSvDeleteDevice}
                editRights={restrictedEditRights}
                deleteRights={deleteRights}
                tzOffset={tzOffset}
              />
            )}
            {/* Servers Pagination */}
            <div className="dp-pagination">
              <p className="dp-pagination__info">
                Showing <span className="dp-pagination__emphasis">{svShowingStart}</span>&ndash;<span className="dp-pagination__emphasis">{svShowingEnd}</span> of{' '}
                <span className="dp-pagination__emphasis">{svTotalFiltered}</span> servers
              </p>
              <div className="dp-pagination__controls">
                <div className="dp-per-page">
                  <label htmlFor="dp-sv-per-page" className="dp-per-page__label">Rows per page:</label>
                  <select
                    id="dp-sv-per-page"
                    value={svPerPage}
                    onChange={(e) => setSvPerPage(parseInt(e.target.value, 10))}
                    className="dp-per-page__select"
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>
                <nav className="dp-page-nav" aria-label="Servers pagination">
                  <button
                    type="button"
                    onClick={() => setSvPage((p) => Math.max(1, p - 1))}
                    disabled={svSafePage <= 1}
                    className="dp-page-btn"
                    aria-label="Previous page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                  {svPageNumbers.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setSvPage(num)}
                      className={`dp-page-num ${num === svSafePage ? 'is-active' : ''}`}
                      aria-label={`Page ${num}`}
                      aria-current={num === svSafePage ? 'page' : undefined}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSvPage((p) => Math.min(svTotalPages, p + 1))}
                    disabled={svSafePage >= svTotalPages}
                    className="dp-page-btn"
                    aria-label="Next page"
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>

            {/* Server Drawer (Add) */}
            <ServerDrawer
              isOpen={svDrawerOpen}
              onClose={() => setSvDrawerOpen(false)}
              onSave={handleSvAddServer}
              areas={svAreas}
              campusId={campusId}
            />
          </>
        ) : null}

      </div>
    </div>
    </div>
    </>
  );
};

export default DevicesPage;
